import type { components } from "@scalty/contracts";

type ModelType = components["schemas"]["ModelType"];

/**
 * The address a caller actually types, and the call that reaches it.
 *
 * The contract returns a host-relative serving path and says so explicitly: "the client composes
 * the absolute URL from its configured origin". The page a customer comes to for the address must
 * therefore compose it — a path alone is not an address, and the deployment page showed nothing
 * else (founder, 2026-09-21, note 41).
 *
 * Every part of the example below is read from the contract: the path from
 * `DeploymentEndpoint.url`, the bearer scheme from `securitySchemes.bearerAuth`, and the body
 * from `ChatCompletionRequest` (`model` is the deployment's own name) or `PredictRequest`
 * (`rows`). The only invented text is the placeholder a caller must replace, and it is written so
 * it cannot be mistaken for a value: `<your key>`, `<column>`, `<value>`.
 */
export function deploymentAddress(origin: string, url: string): string {
  return `${origin.replace(/\/+$/, "")}${url}`;
}

/** How the deployment is spoken to, named where the address is — not in a dialog title (note 37). */
export function deploymentProtocol(modelType: ModelType): string {
  return modelType === "text_generation"
    ? "OpenAI-compatible chat completions"
    : "JSON predictions";
}

export function deploymentCallExample(input: {
  origin: string;
  url: string;
  slug: string;
  modelType: ModelType;
}): string {
  const address = deploymentAddress(input.origin, input.url);
  const body =
    input.modelType === "text_generation"
      ? `{"model":"${input.slug}","messages":[{"role":"user","content":"<your question>"}]}`
      : `{"rows":[{"<column>":"<value>"}]}`;
  return [
    `curl ${address}`,
    `  -H "Authorization: Bearer <your key>"`,
    `  -H "Content-Type: application/json"`,
    `  -d '${body}'`,
  ].join(" \\\n");
}
